import { BriefcaseBusiness, Flag, IdCard, Phone, UserRound } from 'lucide-react'

import type { CreateEntityDialogSection } from '@/components/library-v2/ui/CreateEntityDialogLayout'

import type { EditablePersonField } from '@/types/library/Entity'
import {
  PERSON_FIELDS,
  RELATIONSHIP_STATUS_OPTION_COLORS,
  RELATIONSHIP_STATUS_OPTION_LABELS,
  RELATIONSHIP_STATUS_OPTION_VALUES,
} from '@/types/library/Entity'

export type PersonField = (typeof PERSON_FIELDS)[number]

const PERSON_RELATIONSHIP_STATUS_FIELD = {
  key: 'relationship_status',
  label: 'Relationship Status',
  type: 'dropdown',
  required: false,
  placeholder: 'Select relationship status...',
  options: RELATIONSHIP_STATUS_OPTION_VALUES,
  optionLabels: RELATIONSHIP_STATUS_OPTION_LABELS,
  optionColors: RELATIONSHIP_STATUS_OPTION_COLORS,
} satisfies PersonField

interface PersonFormSectionConfig {
  id: string
  label: string
  description: string
  icon: CreateEntityDialogSection['icon']
  required?: boolean
  fieldKeys: readonly EditablePersonField[]
}

const PERSON_FIELD_BY_KEY = new Map<EditablePersonField, PersonField>(
  [...PERSON_FIELDS, PERSON_RELATIONSHIP_STATUS_FIELD].map((field) => [
    field.key,
    field,
  ]),
)

export const PERSON_FORM_SECTION_CONFIGS = [
  {
    id: 'basics',
    label: 'Profile',
    description: 'Name, title, and primary contact details.',
    icon: UserRound,
    required: true,
    fieldKeys: ['first_name', 'last_name', 'role', 'email', 'phone'],
  },
  {
    id: 'organization',
    label: 'Organization',
    description: 'Associate this person with a company or agency.',
    icon: BriefcaseBusiness,
    fieldKeys: [
      'entity_company_id',
      'entity_agency_id',
      'entity_department_id',
    ],
  },
  {
    id: 'contact',
    label: 'Contact Details',
    description: 'Alternate contact channels and public profile links.',
    icon: Phone,
    fieldKeys: [
      'mobile_phone',
      'office_phone',
      'alternate_email',
      'linkedin_url',
    ],
  },
  {
    id: 'name-details',
    label: 'Additional Names',
    description: 'Preferred names, suffixes, and aliases.',
    icon: IdCard,
    fieldKeys: ['preferred_name', 'middle_name', 'suffixes', 'aliases'],
  },
  {
    id: 'engagement',
    label: 'Engagement',
    description:
      'Prioritization, recent outreach, tags, and relationship context.',
    icon: Flag,
    fieldKeys: [
      'relationship_status',
      'priority',
      'last_contacted_at',
      'tag_ids',
      'description',
    ],
  },
] satisfies readonly PersonFormSectionConfig[]

export const getPersonFields = (
  fieldKeys: readonly EditablePersonField[],
): PersonField[] => {
  return fieldKeys.flatMap((fieldKey) => {
    const field = PERSON_FIELD_BY_KEY.get(fieldKey)
    return field ? [field] : []
  })
}
